import {
  Close,
  ContentCopy,
  ContentPaste,
  MenuBook,
  MicExternalOn,
  Save,
  Send,
  TaskAlt,
} from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AudioIframe from './AudioIframe';
import DynamicAssessmentQuestions from './DynamicAssessmentQuestions';
import QcClassificationTagField from './QcClassificationTagField';
import QcTranscriptDiffPanel from './QcTranscriptDiffPanel';
import TranscriptInlineChipEditor from './TranscriptInlineChipEditor';
import { useTranscriptClipboard } from './useTranscriptClipboard';
import PasteConfirmDialog from './PasteConfirmDialog';
import {
  MOBILE_WAVEFORM_BAR_KEYS,
  MOBILE_WAVEFORM_BARS,
} from './utils/assessmentModalConstants';
import { isRtlLanguage } from '../../utils/rtlLanguages';

const BAR_HEIGHTS = [
  '50%',
  '75%',
  '66%',
  '100%',
  '50%',
  '33%',
  '75%',
  '50%',
  '66%',
  '33%',
  '66%',
  '50%',
  '33%',
];
const BAR_OPACITY = [
  0.2, 0.3, 0.4, 0.6, 0.4, 0.3, 0.5, 0.7, 0.4, 0.2, 0.5, 0.8, 0.4,
];

export default function QcAssessmentMobileLayout({
  onClose,
  taskMeta,
  sortedQuestions,
  readOnly,
  linkedTranscriptQuestionIdSet,
  linkedTranscriptQuestionIds = [],
  onPlainTranscriptFromLinkedField,
  transcriptSegments,
  onTranscriptSegmentsChange,
  transcriptInputRef,
  classificationTags = [],
  onAddClassificationTag,
  classificationTagOptions = [],
  showTranscriptFields = true,
  vendorPlainForDiff = '',
  transcriptChangePercentage = 0,
  transcriptDiffExpanded = false,
  onTranscriptDiffExpandedChange,
  onTranscriptEditorCommit,
  onLinkedTranscriptFieldCommitted,
  onSaveAndNext,
  onSubmitAndNext,
  onSubmitAndClose,
  isSaving = false,
  isSubmitting = false,
  reviewer = false,
  onReviewApprove,
  onReviewApproveNext,
  onReviewReject,
  onReviewSkip,
  isPendingReview = false,
}) {
  const showStandAloneClassification =
    !showTranscriptFields &&
    Array.isArray(classificationTagOptions) &&
    classificationTagOptions.length > 0;

  const transcriptDir = isRtlLanguage(taskMeta.language) ? 'rtl' : 'ltr';

  const {
    clipboardMessage,
    handleCopyVendorTranscript,
    handlePasteIntoUpdatedTranscript,
    pasteConfirmReasons,
    confirmPendingPaste,
    cancelPendingPaste,
  } = useTranscriptClipboard({
    vendorText: taskMeta.originalTranscript,
    readOnly,
    transcriptInputRef,
    transcriptSegments,
    onTranscriptEditorCommit,
  });

  return (
    <Box
      className="flex min-h-0 flex-1 flex-col overflow-hidden bg-[#f8f9ff] font-[Inter,system-ui,sans-serif] text-[#0d1c2e] dark:bg-gray-950 dark:text-gray-100"
      sx={{ height: '100%', maxHeight: '100dvh' }}
    >
      <Box
        component="header"
        className="flex shrink-0 items-center justify-between px-3 py-2.5 shadow-[0_2px_10px_rgba(0,0,0,0.05)] dark:shadow-[0_2px_12px_rgba(0,0,0,0.35)]"
        sx={(theme) => ({
          bgcolor:
            theme.palette.mode === 'dark'
              ? theme.palette.background.default
              : '#f8f9ff',
          zIndex: 10,
        })}
      >
        <Box className="flex min-w-0 items-center gap-3">
          <MicExternalOn
            className="shrink-0 text-[#24389c] dark:text-[#9aacf0]"
            sx={{ fontSize: 28 }}
          />
          <Box className="flex min-w-0 flex-col gap-0.5">
            <Typography
              className="font-[Manrope] text-lg font-extrabold leading-tight tracking-tight text-[#24389c] dark:text-[#9aacf0]"
              sx={{ fontFamily: 'Manrope, Inter, sans-serif' }}
            >
              Assessment Details
            </Typography>
            {taskMeta.assignedTo ? (
              <Typography
                variant="caption"
                noWrap
                sx={{ color: 'text.secondary', fontSize: '0.65rem', maxWidth: 200 }}
                title={taskMeta.assignedTo}
              >
                Assignee: {taskMeta.assignedTo}
              </Typography>
            ) : null}
          </Box>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="Close assessment"
          className="text-[rgba(69,70,82,0.7)] hover:bg-red-50 hover:text-red-600 dark:text-gray-400 dark:hover:bg-red-950/40 dark:hover:text-red-400"
        >
          <Close />
        </IconButton>
      </Box>

      <Box
        component="main"
        className="custom-scrollbar flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-3 pb-3 pt-3"
        sx={{ maxWidth: 448, mx: 'auto', width: '100%' }}
      >
        <Box
          className="flex flex-col gap-2 rounded-xl border border-[rgba(197,197,212,0.3)] bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900"
          sx={{ bgcolor: 'background.paper' }}
        >
          {taskMeta.filePath ? (
            <Box className="w-full overflow-hidden rounded-lg border border-[rgba(197,197,212,0.35)] dark:border-gray-600">
              <AudioIframe
                audioFileURL={taskMeta.filePath}
                title="Spectrogram"
                height={200}
              />
            </Box>
          ) : (
            <Box
              className="relative flex h-16 w-full items-end gap-0.5 overflow-hidden rounded-lg px-1"
              sx={(theme) => ({
                bgcolor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(36, 56, 156, 0.12)'
                    : '#eff4ff',
              })}
            >
              {MOBILE_WAVEFORM_BARS.map((_, i) => (
                <Box
                  key={MOBILE_WAVEFORM_BAR_KEYS[i]}
                  sx={{
                    flex: 1,
                    height: BAR_HEIGHTS[i % BAR_HEIGHTS.length],
                    bgcolor: `rgba(36,56,156,${BAR_OPACITY[i % BAR_OPACITY.length]})`,
                    borderRadius: '2px',
                  }}
                />
              ))}
              <Box
                className="absolute inset-y-0 z-10 w-0.5 bg-[#ba1a1a]"
                sx={{ left: '33%' }}
              />
            </Box>
          )}
        </Box>

        <Stack spacing={1.75} sx={{ width: '100%' }}>
          {showTranscriptFields ? (
            <Box
              className="rounded-xl border border-[rgba(197,197,212,0.3)] p-3 dark:border-gray-700"
              sx={(theme) => ({
                position: 'sticky',
                top: 0,
                zIndex: 5,
                bgcolor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(36, 56, 156, 0.14)'
                    : '#eff4ff',
                alignSelf: 'stretch',
                width: '100%',
                boxShadow:
                  theme.palette.mode === 'dark'
                    ? '0 4px 12px rgba(0, 0, 0, 0.35)'
                    : '0 4px 12px rgba(13, 28, 46, 0.06)',
              })}
            >
              <Box className="mb-2 flex items-center justify-between">
                <Typography className="text-[10px] font-extrabold uppercase tracking-widest text-[#565c84] dark:text-gray-400">
                  Vendor Transcript
                </Typography>
                <IconButton
                  size="small"
                  className="text-[#24389c]"
                  aria-label="Copy vendor transcript"
                  onClick={() => void handleCopyVendorTranscript()}
                >
                  <ContentCopy sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
              <Typography
                dir={transcriptDir}
                className="text-[13px] font-medium leading-relaxed text-[#454652] dark:text-gray-300"
              >
                {taskMeta.originalTranscript}
              </Typography>
            </Box>
          ) : null}

          {showTranscriptFields ? (
            <Stack spacing={0.75} sx={{ width: '100%' }}>
              <QcTranscriptDiffPanel
                expanded={transcriptDiffExpanded}
                onToggle={onTranscriptDiffExpandedChange}
                oldValue={vendorPlainForDiff}
                committedChangePercentage={transcriptChangePercentage}
                readOnly={readOnly}
                dir={transcriptDir}
              />
              <Typography className="ml-0.5 text-[10px] font-extrabold uppercase tracking-widest text-[#24389c]">
                Transcript Update
                {transcriptChangePercentage > 0 ? (
                  <Typography
                    component="span"
                    sx={{
                      ml: 0.75,
                      fontSize: 'inherit',
                      fontWeight: 800,
                      color: 'primary.main',
                    }}
                  >
                    (Change: {transcriptChangePercentage.toFixed(2)}%)
                  </Typography>
                ) : null}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: { xs: 0.25, sm: 0.5 },
                  alignItems: 'flex-start',
                  width: '100%',
                  minWidth: 0,
                }}
              >
                <TranscriptInlineChipEditor
                  ref={transcriptInputRef}
                  id="qc-mobile-updated-transcript-editor"
                  segments={transcriptSegments}
                  onSegmentsChange={onTranscriptSegmentsChange}
                  readOnly={readOnly}
                  minRows={3}
                  placeholder="Enter correction here..."
                  aria-label="Updated transcript"
                  dir={transcriptDir}
                  sx={{ flex: 1, minWidth: 0 }}
                />
                <Tooltip title="Paste from clipboard" arrow>
                  <span>
                    <IconButton
                      type="button"
                      size="small"
                      aria-label="Paste from clipboard"
                      disabled={readOnly}
                      onClick={() => void handlePasteIntoUpdatedTranscript()}
                      sx={{
                        color: '#24389c',
                        mt: { xs: 0.25, sm: 0.5 },
                        flexShrink: 0,
                      }}
                    >
                      <ContentPaste sx={{ fontSize: 20 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              {clipboardMessage ? (
                <Typography
                  role="status"
                  variant="caption"
                  sx={{
                    display: 'block',
                    color:
                      clipboardMessage.severity === 'error'
                        ? 'error.main'
                        : clipboardMessage.severity === 'success'
                          ? 'success.main'
                          : 'text.secondary',
                  }}
                >
                  {clipboardMessage.text}
                </Typography>
              ) : null}
              <PasteConfirmDialog
                reasons={pasteConfirmReasons}
                onConfirm={confirmPendingPaste}
                onCancel={cancelPendingPaste}
              />
              <QcClassificationTagField
                id="qc-mobile-classification-tag"
                options={classificationTagOptions}
                selectedTags={showTranscriptFields ? [] : classificationTags}
                onAddTag={onAddClassificationTag}
                readOnly={readOnly}
                size="small"
                sx={{ mt: 0 }}
              />
            </Stack>
          ) : null}

          <Box className="mt-1 flex flex-col gap-2">
            <Typography className="px-1 text-xs font-black uppercase tracking-[0.15em] text-[#0d1c2e] dark:text-gray-100">
              Assessment Questions
            </Typography>
            {readOnly ? (
              <Typography className="px-1 text-[10px] font-bold uppercase tracking-wider text-[#24389c] dark:text-[#b4c2f5]">
                Submitted / read-only
              </Typography>
            ) : null}
            <DynamicAssessmentQuestions
              questions={sortedQuestions}
              readOnly={readOnly}
              variant="mobile"
              linkedTranscriptQuestionIdSet={linkedTranscriptQuestionIdSet}
              linkedTranscriptQuestionIds={linkedTranscriptQuestionIds}
              onPlainTranscriptFromLinkedField={
                onPlainTranscriptFromLinkedField
              }
              onLinkedTranscriptFieldCommitted={
                onLinkedTranscriptFieldCommitted
              }
            />
          </Box>
        </Stack>
      </Box>

      <Box
        component="nav"
        className="flex shrink-0 flex-col gap-2.5 border-t border-[rgba(197,197,212,0.2)] bg-white px-3 pt-3 pb-7 dark:border-gray-700 dark:bg-gray-900"
        sx={{ pb: 'max(32px, env(safe-area-inset-bottom, 0px))' }}
      >
        {reviewer ? (
          <>
            <Box className="grid grid-cols-2 gap-2.5">
              <Button variant="outlined" color="error" fullWidth onClick={onReviewReject} disabled={isPendingReview}
                className="rounded-xl py-3 text-[13px] font-bold" sx={{ textTransform: 'none' }}>
                {isPendingReview ? 'Saving...' : 'Reject'}
              </Button>
              <Button variant="outlined" color="success" fullWidth onClick={onReviewApproveNext} disabled={isPendingReview}
                className="rounded-xl py-3 text-[13px] font-bold" sx={{ textTransform: 'none' }}>
                {isPendingReview ? 'Saving...' : 'Approve & Next'}
              </Button>
            </Box>
            <Button variant="contained" color="success" size="large" fullWidth onClick={onReviewApprove} disabled={isPendingReview}
              className="rounded-xl py-4 text-sm font-extrabold shadow-xl" sx={{ textTransform: 'none' }}>
              {isPendingReview ? 'Saving...' : 'Approve'}
            </Button>
            <Button variant="text" fullWidth onClick={onReviewSkip} disabled={isPendingReview}
              className="rounded-xl py-2 text-[13px] font-bold text-[#454652] dark:text-gray-200" sx={{ textTransform: 'none' }}>
              Skip
            </Button>
          </>
        ) : readOnly ? (
          <Button
            variant="contained"
            size="large"
            fullWidth
            className="rounded-xl py-4 text-sm font-extrabold shadow-xl"
            sx={{
              bgcolor: '#24389c',
              color: '#ffffff',
              textTransform: 'none',
              '&:hover': { bgcolor: '#1e2f85' },
            }}
            onClick={onClose}
          >
            Close
          </Button>
        ) : (
          <>
            <Box className="grid grid-cols-2 gap-2.5">
              <Button
                variant="outlined"
                startIcon={<Save />}
                className="rounded-xl border-2 border-[#3f51b5] py-3 text-[13px] font-bold text-[#24389c] dark:border-[#8b9fe8] dark:text-[#b4c2f5]"
                sx={{ textTransform: 'none', borderColor: '#3f51b5' }}
                onClick={onSaveAndNext}
                disabled={isSaving || isSubmitting}
              >
                {isSaving ? 'SAVING...' : 'SAVE & NEXT'}
              </Button>
              <Button
                variant="contained"
                startIcon={<Send />}
                className="rounded-xl py-3 text-[13px] font-bold shadow-md"
                sx={{
                  bgcolor: '#3f51b5',
                  color: '#cacfff',
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#303f9f' },
                }}
                onClick={onSubmitAndNext}
                disabled={isSubmitting || isSaving}
              >
                {isSubmitting ? 'SUBMITTING...' : 'SUBMIT & NEXT'}
              </Button>
            </Box>
            <Button
              variant="contained"
              size="large"
              fullWidth
              startIcon={<TaskAlt sx={{ fontSize: 20 }} />}
              className="rounded-xl py-4 text-sm font-extrabold shadow-xl"
              sx={{
                bgcolor: '#24389c',
                color: '#ffffff',
                textTransform: 'none',
                '& .MuiButton-startIcon': { color: '#ffffff' },
                '&:hover': { bgcolor: '#1e2f85' },
              }}
              onClick={onSubmitAndClose}
              disabled={isSubmitting || isSaving}
            >
              {isSubmitting ? 'SUBMITTING...' : 'SUBMIT & CLOSE'}
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
}
